(() => {
  let updateTimer = null;

  const DAY_LAST_MINUTE = 899;
  const NIGHT_FIRST_MINUTE = 900;
  const EARLY_MORNING_FIRST_MINUTE = 240;
  const DEFAULT_ON_TIME = '06:00';
  const DEFAULT_OFF_TIME = '22:00';
  const HALF_TRACK = 50;
  const sectionStates = new WeakMap();

  const uiCopy = {
    de: {
      group: 'Zeitansicht',
      live: 'LIVE',
      travel: 'ZEITREISE',
      range: 'Uhrzeit für die Zeitreise auswählen',
      hint: 'Zieh den Punkt durch den Tag und entdecke jeden ON•OFF Moment.',
    },
    en: {
      group: 'Time view',
      live: 'LIVE',
      travel: 'TIME TRAVEL',
      range: 'Choose a time for time travel',
      hint: 'Drag the dot through the day and discover every ON•OFF moment.',
    },
    fr: {
      group: 'Affichage horaire',
      live: 'EN DIRECT',
      travel: 'VOYAGE TEMPOREL',
      range: 'Choisir une heure pour le voyage temporel',
      hint: 'Fais glisser le point et découvre chaque moment ON•OFF.',
    },
    it: {
      group: 'Vista oraria',
      live: 'LIVE',
      travel: 'VIAGGIO NEL TEMPO',
      range: 'Scegli un orario per il viaggio nel tempo',
      hint: 'Sposta il punto nella giornata e scopri ogni momento ON•OFF.',
    },
  };

  const parseTime = (value) => {
    const [hours, minutes] = String(value || '').trim().split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return hours * 60 + minutes;
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const formatTime = (minutes) => {
    const safeMinutes = ((Number(minutes) % 1440) + 1440) % 1440;
    const hours = Math.floor(safeMinutes / 60);
    const mins = safeMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  const currentMinutes = () => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  };

  const phraseOptions = (section, type) => {
    const template = section.querySelector(`[data-middle-phrases="${type}"]`);
    if (!template) return [];

    return Array.from(template.content.querySelectorAll('[data-phrase-option]'))
      .map((option) => ({
        title: option.querySelector('[data-phrase-title]')?.textContent.trim() || '',
        text: option.querySelector('[data-phrase-text]')?.textContent.trim() || '',
      }))
      .filter((option) => option.title || option.text);
  };

  const randomPhrase = (options, previous) => {
    if (!options.length) return null;
    const alternatives = options.filter(
      (option) => !previous || option.title !== previous.title || option.text !== previous.text
    );
    const pool = alternatives.length ? alternatives : options;
    return pool[Math.floor(Math.random() * pool.length)];
  };

  const renderPhrase = (panel, phrase) => {
    if (!panel || !phrase) return;
    const title = panel.querySelector('[data-middle-title]');
    const text = panel.querySelector('[data-middle-text]');
    if (title) title.textContent = phrase.title;
    if (text) {
      text.textContent = '';
      if (phrase.text) {
        const paragraph = document.createElement('p');
        paragraph.textContent = phrase.text;
        text.appendChild(paragraph);
      }
    }
  };

  const ensureState = (section) => {
    let state = sectionStates.get(section);
    if (state) return state;

    state = {
      mode: 'live',
      travelMinutes: 0,
      lastPanel: null,
      phrasePools: {
        'middle-off': phraseOptions(section, 'off'),
        'middle-on': phraseOptions(section, 'on'),
      },
      selectedPhrases: {
        'middle-off': null,
        'middle-on': null,
      },
      controls: null,
    };

    ['middle-off', 'middle-on'].forEach((key) => {
      state.selectedPhrases[key] = randomPhrase(state.phrasePools[key], null);
      renderPhrase(section.querySelector(`[data-panel="${key}"]`), state.selectedPhrases[key]);
    });

    sectionStates.set(section, state);
    return state;
  };

  const getPhase = (section, minutes) => {
    const onTime = parseTime(section.dataset.timeStart || DEFAULT_ON_TIME) ?? parseTime(DEFAULT_ON_TIME);
    const offTime = parseTime(section.dataset.timeEnd || DEFAULT_OFF_TIME) ?? parseTime(DEFAULT_OFF_TIME);

    const period =
      minutes >= onTime && minutes <= DAY_LAST_MINUTE
        ? 'day'
        : minutes >= NIGHT_FIRST_MINUTE && minutes < offTime
          ? 'night'
          : 'transition';

    const panel =
      minutes >= onTime && minutes <= DAY_LAST_MINUTE
        ? 'on'
        : minutes >= NIGHT_FIRST_MINUTE && minutes <= offTime
          ? 'off'
          : minutes >= EARLY_MORNING_FIRST_MINUTE && minutes < onTime
            ? 'middle-on'
            : 'middle-off';

    let position = HALF_TRACK;
    if (period === 'day') {
      const duration = DAY_LAST_MINUTE - onTime;
      position = duration <= 0 ? 0 : clamp(((minutes - onTime) / duration) * HALF_TRACK, 0, HALF_TRACK);
    } else if (period === 'night') {
      const duration = offTime - NIGHT_FIRST_MINUTE;
      position = duration <= 0
        ? HALF_TRACK
        : HALF_TRACK + clamp(((minutes - NIGHT_FIRST_MINUTE) / duration) * HALF_TRACK, 0, HALF_TRACK);
    }

    return { period, panel, position };
  };

  const rotateMiddlePhrase = (section, state, panelKey) => {
    if (!panelKey.startsWith('middle-')) return;
    const next = randomPhrase(state.phrasePools[panelKey], state.selectedPhrases[panelKey]);
    state.selectedPhrases[panelKey] = next;
    renderPhrase(section.querySelector(`[data-panel="${panelKey}"]`), next);
  };

  const showPanel = (section, state, panelKey, rotatePhrase) => {
    if (rotatePhrase && state.lastPanel !== panelKey) {
      rotateMiddlePhrase(section, state, panelKey);
    }

    section.querySelectorAll('[data-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.panel !== panelKey;
    });
    state.lastPanel = panelKey;
  };

  const applyTime = (section, minutes, options = {}) => {
    const state = ensureState(section);
    const { period, panel, position } = getPhase(section, minutes);

    section.classList.remove(
      'day-night-section--period-day',
      'day-night-section--period-transition',
      'day-night-section--period-night'
    );
    section.classList.add(`day-night-section--period-${period}`);

    const currentTime = section.querySelector('.day-night-section__current-time');
    if (currentTime) {
      currentTime.style.setProperty('--day-night-section-current-time-position', position);
      const label = currentTime.querySelector('[data-current-time]');
      if (label) label.textContent = formatTime(minutes);
      currentTime.classList.add('day-night-section__current-time--ready');
    }

    if (section.dataset.showPanelByTime === 'true') {
      showPanel(section, state, panel, Boolean(options.rotatePhrase));
    }

    if (state.controls) {
      state.controls.root.dataset.activePeriod = period;
      state.controls.root.dataset.activePanel = panel;
    }
  };

  const labelsForPage = () => {
    const language = (document.documentElement.lang || 'de').toLowerCase().slice(0, 2);
    return uiCopy[language] || uiCopy.de;
  };

  const isRitualPage = () => /\/pages\/rituals\/?$/.test(window.location.pathname);

  const setMode = (section, mode) => {
    const state = ensureState(section);
    if (!state.controls || state.mode === mode) return;

    state.mode = mode;
    state.lastPanel = null;
    const isTravel = mode === 'travel';

    section.classList.toggle('day-night-section--time-travel-active', isTravel);
    state.controls.liveButton.classList.toggle('is-active', !isTravel);
    state.controls.travelButton.classList.toggle('is-active', isTravel);
    state.controls.liveButton.setAttribute('aria-pressed', String(!isTravel));
    state.controls.travelButton.setAttribute('aria-pressed', String(isTravel));
    state.controls.travelPanel.hidden = !isTravel;

    if (isTravel) {
      const livePanel = getPhase(section, currentMinutes()).panel;
      state.travelMinutes = livePanel.startsWith('middle-') ? 540 : 0;
      state.controls.range.value = String(state.travelMinutes);
      applyTime(section, state.travelMinutes, { rotatePhrase: true });
      window.requestAnimationFrame(() => state.controls.range.focus({ preventScroll: true }));
    } else {
      applyTime(section, currentMinutes());
    }
  };

  const createTimeTravelControls = (section) => {
    if (!isRitualPage() || section.querySelector('[data-time-travel-controls]')) return;

    const clock = section.querySelector('.day-night-section__clock');
    if (!clock) return;

    const copy = labelsForPage();
    const root = document.createElement('div');
    root.className = 'day-night-section__time-travel';
    root.dataset.timeTravelControls = '';
    root.innerHTML = `
      <div class="day-night-section__mode-switch" role="group" aria-label="${copy.group}">
        <button class="day-night-section__mode-button is-active" type="button" data-time-mode="live" aria-pressed="true">
          <span class="day-night-section__live-dot" aria-hidden="true"></span>
          ${copy.live}
        </button>
        <button class="day-night-section__mode-button" type="button" data-time-mode="travel" aria-pressed="false">
          ${copy.travel}
        </button>
      </div>
      <div class="day-night-section__travel-panel" data-time-travel-panel hidden>
        <label class="day-night-section__range-label">
          <span class="visually-hidden">${copy.range}</span>
          <input class="day-night-section__time-range" type="range" min="0" max="1439" step="15" value="0" data-time-travel-range aria-label="${copy.range}">
        </label>
        <div class="day-night-section__range-markers" aria-hidden="true">
          <span>00.00</span><span>06.00</span><span>12.00</span><span>18.00</span><span>24.00</span>
        </div>
        <p class="day-night-section__travel-hint">${copy.hint}</p>
      </div>
    `;

    clock.insertAdjacentElement('afterend', root);
    section.classList.add('day-night-section--supports-time-travel');

    const state = ensureState(section);
    state.controls = {
      root,
      liveButton: root.querySelector('[data-time-mode="live"]'),
      travelButton: root.querySelector('[data-time-mode="travel"]'),
      travelPanel: root.querySelector('[data-time-travel-panel]'),
      range: root.querySelector('[data-time-travel-range]'),
    };

    state.controls.liveButton.addEventListener('click', () => setMode(section, 'live'));
    state.controls.travelButton.addEventListener('click', () => setMode(section, 'travel'));
    state.controls.range.addEventListener('input', (event) => {
      const nextMinutes = Number(event.currentTarget.value);
      state.travelMinutes = Number.isNaN(nextMinutes) ? 0 : nextMinutes;
      applyTime(section, state.travelMinutes, { rotatePhrase: true });
    });
  };

  const updateSections = () => {
    const sections = document.querySelectorAll('.day-night-section');
    if (!sections.length) return;

    sections.forEach((section) => {
      const state = ensureState(section);
      createTimeTravelControls(section);
      const minutes = state.mode === 'travel' ? state.travelMinutes : currentMinutes();
      applyTime(section, minutes);
    });
  };

  const init = () => {
    updateSections();
    if (updateTimer) window.clearInterval(updateTimer);
    updateTimer = window.setInterval(updateSections, 60000);
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
